import React, { useContext } from "react";
import styled, { keyframes } from "styled-components";
import { ProductContext } from "../context/ProductContext";
import Header from "../layouts/header/Header";
import { useNavigation } from "../utils/navigation";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/footer/Footer";
import { useTranslation } from "react-i18next";
import { transportMethods } from "../utils/global_const";

const ThankYouContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
  background-color: var(--bg-100);
  min-height: 100vh;
`;

const pulseRing = keyframes`
  0% { transform: scale(0.9); opacity: 0.6; }
  100% { transform: scale(1.9); opacity: 0; }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Badge = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: var(--success-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: var(--bg-100);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: var(--success-color);
    animation: ${pulseRing} 1.8s ease-out infinite;
  }
`;

const Greeting = styled.h1`
  font-size: 1.7rem;
  color: var(--text-100);
  margin: 16px 0 4px;
  animation: ${fadeUp} 0.5s 0.1s both;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--text-200);
  margin-bottom: 20px;
  animation: ${fadeUp} 0.5s 0.2s both;
`;

const DeliveryCard = styled.div`
  width: 100%;
  max-width: 560px;
  background-color: var(--bg-200);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 24px 20px;
  animation: ${fadeUp} 0.5s 0.3s both;
`;

const EstimateRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--bg-100);
`;
const EstimateIcon = styled.div`
  font-size: 1.6rem;
`;
const EstimateText = styled.div`
  text-align: left;
`;
const EstimateLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-200);
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;
const EstimateValue = styled.div`
  font-size: 1.05rem;
  color: var(--text-100);
  font-weight: bold;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  text-align: left;
`;

const InfoBlock = styled.div`
  grid-column: ${(props) => (props.$full ? "1 / -1" : "auto")};
`;
const InfoLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-200);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
`;
const InfoValue = styled.div`
  font-size: 0.95rem;
  color: var(--text-100);
  line-height: 1.4;
`;

const Button = styled.button`
  width: 100%;
  margin-top: 20px;
`;

function estimateDeliveryRange(transportMethodLabel, locale) {
  const match = transportMethods.find((m) => transportMethodLabel?.includes(m.name));
  const days = match ? match.days : 5;
  const from = new Date();
  from.setDate(from.getDate() + days);
  const to = new Date();
  to.setDate(to.getDate() + days + 2);
  const opts = { day: "numeric", month: "short" };
  try {
    return `${from.toLocaleDateString(locale, opts)} – ${to.toLocaleDateString(
      locale,
      opts
    )}`;
  } catch {
    return `${from.toDateString()} – ${to.toDateString()}`;
  }
}

const ThankYouPage = () => {
  const { t, i18n } = useTranslation();
  const { createdOrder } = useContext(ProductContext);
  const { goToOrderHistory } = useNavigation();
  const navigate = useNavigate();

  const address = createdOrder?.address;

  return (
    <>
      <Header />
      <ThankYouContainer>
        <Badge>✓</Badge>
        <Greeting>
          {createdOrder?.first_name
            ? t("THANK_YOU.GREETING_NAME", {
                name: createdOrder.first_name,
                defaultValue: "Thanks, {{name}}!",
              })
            : t("THANK_YOU.TITLE")}
        </Greeting>
        <Subtitle>{t("THANK_YOU.SUBTITLE")}</Subtitle>

        {createdOrder && (
          <DeliveryCard>
            <EstimateRow>
              <EstimateIcon>📦</EstimateIcon>
              <EstimateText>
                <EstimateLabel>
                  {t("THANK_YOU.ESTIMATED_DELIVERY", "Estimated delivery")}
                </EstimateLabel>
                <EstimateValue>
                  {estimateDeliveryRange(createdOrder.transport_method, i18n.language)}
                </EstimateValue>
              </EstimateText>
            </EstimateRow>

            <InfoGrid>
              <InfoBlock>
                <InfoLabel>{t("THANK_YOU.ORDER_DETAILS")}</InfoLabel>
                <InfoValue>#{createdOrder.id}</InfoValue>
              </InfoBlock>
              <InfoBlock>
                <InfoLabel>{t("THANK_YOU.TOTAL", "Total")}</InfoLabel>
                <InfoValue>
                  {createdOrder.total_price} {createdOrder.currency}
                </InfoValue>
              </InfoBlock>
              <InfoBlock>
                <InfoLabel>{t("THANK_YOU.PAYMENT_METHOD")}</InfoLabel>
                <InfoValue>{createdOrder.payment_method}</InfoValue>
              </InfoBlock>
              <InfoBlock>
                <InfoLabel>{t("THANK_YOU.TRANSPORT_METHOD")}</InfoLabel>
                <InfoValue>{createdOrder.transport_method}</InfoValue>
              </InfoBlock>
              {address && (
                <InfoBlock $full>
                  <InfoLabel>
                    {t("THANK_YOU.SHIPPING_ADDRESS", "Shipping address")}
                  </InfoLabel>
                  <InfoValue>
                    {address.street} {address.building_number}
                    {address.secondary_street
                      ? `, ${address.secondary_street}`
                      : ""}
                    , {address.postal_code} {address.city}, {address.country}
                  </InfoValue>
                </InfoBlock>
              )}
            </InfoGrid>

            <Button onClick={() => navigate(goToOrderHistory())}>
              {t("THANK_YOU.VIEW_ORDER", "View Order")}
            </Button>
          </DeliveryCard>
        )}
      </ThankYouContainer>
      <Footer />
    </>
  );
};

export default ThankYouPage;
